<template>
  <div class="view-job-details">
    <b-alert v-if="jobIsInActive" variant="warning" show style="text-align: center"
      >This job is no longer active
    </b-alert>

    <b-alert v-if="jobIsPreview" variant="secondary" show style="text-align: center; color: #66676b"
      >This job is under review and not public yet</b-alert
    >

    <JobsPageContent :job="job" :is-careers-page="true" />

    <Container width="big" spacing="small" plr-none>
      <StickyBlock v-if="jobIsActive">
        <b-row class="p-16 border-top">
          <b-col class="d-flex align-items-center justify-content-end">
            <button class="btn px-60" @click.prevent="isApplyOpen = true">
              Apply
            </button>
          </b-col>
        </b-row>
      </StickyBlock>
    </Container>

    <ScrapedJobsApplyModal
      v-if="isScraped()"
      v-model="isApplyOpen"
      :job="job"
      @submitted="onSubmitted"
    />
    <JobsApplyModal v-else v-model="isApplyOpen" :job="job" @submitted="onSubmitted" />

    <ScrapedJobsSignUpModal v-model="isSignUpOpen" />
    <ApplicationSentModal
      v-model="isApplicationSent"
      :success-data="successData"
      :is-careers-page="true"
    />
  </div>
</template>

<script>
import Container from '@/components/Container';
import StickyBlock from '@/components/StickyBlock';
import JobsPageContent from '@/components/jobs/JobsPageContent';
import JobsApplyModal from '@/modals/jobs/JobsApply';
import ScrapedJobsApplyModal from '@/modals/jobs/ScrapedJobsApply';
import ScrapedJobsSignUpModal from '@/modals/jobs/ScrapedJobsSignUp';
import ApplicationSentModal from '@/modals/ApplicationSent';
import { buildMetaHead } from '@/helpers/meta';
import uniq from 'lodash/uniq';

export default {
  name: 'PageJobsId',
  layout: 'careers',
  components: {
    Container,
    StickyBlock,
    JobsPageContent,
    JobsApplyModal,
    ScrapedJobsApplyModal,
    ScrapedJobsSignUpModal,
    ApplicationSentModal
  },
  head() {
    return buildMetaHead({
      title: this.job.title + ' | ' + this.job.company.name,
      description: this.job.description
        .replace(/(<([^>]+)>)/gi, ' ')
        .substr(0, 255)
        .trim(),
      path: this.job.url,
      imageUrl: this.job.socialImageUrl,
      googleJob: this.mapToGoogleJob(this.job)
    });
  },
  data: () => ({
    isApplyOpen: false,
    isSignUpOpen: false,
    isApplicationSent: false,
    applicant: {}
  }),
  computed: {
    userIsLoggedIn() {
      return this.$store.getters['user/isLoggedIn'];
    },
    job() {
      return this.$store.state.jobs.job;
    },
    jobIsInActive() {
      return ['EXPIRED', 'STOPPED'].includes(this.job.status);
    },
    jobIsPreview() {
      return this.job.isDraft || this.job.isPending;
    },
    jobIsActive() {
      return this.job.status === 'ACTIVE';
    },
    successData() {
      return {
        title: 'Application sent!',
        subtitle:
          this.job.company.name +
          ' will contact you if your skills and experiences are a strong match.',
        applicant: this.applicant,
        image: 'ilustration01.jpg',
        link: `//${this.job.company.externalDomain}`,
        label: 'Great!',
        width: 360
      };
    }
  },
  async asyncData({ params, store, error }) {
    try {
      await store.dispatch('jobs/get', params.id);
    } catch (err) {
      error(err);
    }
  },
  methods: {
    isScraped() {
      return !!this.job.scraped;
    },
    async onSubmitted(data) {
      this.applicant = {
        name: data.name,
        email: data.email,
        phone: data.phone
      };

      this.isApplyOpen = false;
      if (this.isScraped()) {
        if (!this.userIsLoggedIn) {
          this.isSignUpOpen = true;
        }
      } else {
        this.isApplicationSent = true;
      }

      // reload profiles
      await this.$store.dispatch('user/load');
    },
    positionTypes() {
      return this.$store.getters['meta/jobPositionTypes'].map((item) => ({
        text: item.label,
        value: item.id
      }));
    },
    mapGoogleEmploymentType(jobPositionTypes) {
      const positionTypes = this.positionTypes();

      let tempArr = [];
      jobPositionTypes.forEach((element) => {
        tempArr.push(
          positionTypes.find((item) => {
            return item.value === element;
          })
        );
      });

      let googleArr = [];

      tempArr.forEach((item) => {
        if (item.text === 'Full-time') {
          googleArr.push('FULL_TIME');
        } else if (item.text === 'Part-time') {
          googleArr.push('PART_TIME');
        } else if (item.text === 'Internship') {
          googleArr.push('INTERN');
        } else if (item.text === 'Freelance') {
          googleArr.push('CONTRACTOR');
        } else if (item.text === 'Advisory board') {
          googleArr.push('PART_TIME');
          googleArr.push('CONTRACTOR');
        } else {
          googleArr.push('OTHER');
        }
      });
      // remove duplicates
      const uniqueArr = uniq(googleArr);

      return uniqueArr;
    },
    mapToGoogleJob(job) {
      // console.log('mapToGoogleJob!')
      if (job.status !== 'ACTIVE') {
        return null;
      }

      let googleJobObj = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting'
      };

      // Required properties
      googleJobObj.title = job.title;
      googleJobObj.datePosted = job.createdAt;
      googleJobObj.description = job.description;
      googleJobObj.validThrough = job.expirationDate;
      googleJobObj.hiringOrganization = {
        '@type': 'Organization',
        name: job.company.name,
        sameAs: job.company.website,
        logo: job.company.logoImage.thumbnailSizeUrl
      };

      if (!job.isRemote && job.location) {
        // "Standard job posting"

        googleJobObj.jobLocation = {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: job.location.address,
            addressLocality: job.location.locality,
            addressRegion: job.location.locality,
            postalCode: job.location.postalCode,
            addressCountry: job.location.country
          }
        };
      } else if (job.isRemote) {
        // "Work from home job posting"
        googleJobObj.jobLocationType = 'TELECOMMUTE';
      }

      // Recommended properties
      googleJobObj.directApply = 'true';
      googleJobObj.employmentType = this.mapGoogleEmploymentType(job.jobPositionTypes);
      googleJobObj.identifier = {
        '@type': 'PropertyValue',
        name: job.company.name,
        value: job.id
      };
      return JSON.stringify(googleJobObj);
    }
  }
};
</script>

<style scoped lang="scss">
.view-job-details {
  @include media-breakpoint-down(sm) {
    .bullet-inline-list {
      margin-top: rem(8px);

      a {
        display: block;
        margin-bottom: rem(1px);

        &:before {
          display: none;
        }
      }
    }
  }

  .company-image {
    max-width: 100%;
  }
}
</style>
